import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, PlayCircle, Phone, Code2, Bot, Search, Server,
  Palette, Headphones, Users, Monitor, MessageSquare, TrendingUp,
} from "lucide-react";
import { BRAND } from "@/config/brand";
import PlugitAIChat from "@/pages/PlugitAIChat";
import MobilePocketPopup from "@/pages/MobilePocketPopup";

// ─── Custom-tier palette: charcoal + brushed silver + electric blue + gold-for-money-only
const INK       = "#0A0A0C";
const CHAR      = "#141418";
const PANEL     = "#0F0F13";
const SILVER    = "#C7CBD1";
const SILVER_HI = "#EDEDF0";
const BLUE      = "#00A3FF";
const BLUE_HI   = "#4CC3FF";
const BLUE_GLOW = "rgba(0,163,255,0.4)";
const GOLD      = "#F5B800";
const GOLD_HI   = "#FFCE3D";
const DIM       = "rgba(199,203,209,0.7)";
const FADE      = "rgba(199,203,209,0.45)";

const SERVICES = [
  { icon: Code2,      title: "WEB DESIGN",     body: "Custom websites that look amazing and convert visitors." },
  { icon: Bot,        title: "AI AUTOMATION",  body: "Smart bots and automations that save time and close more sales." },
  { icon: Search,     title: "SEO SERVICES",   body: "Rank higher, get found faster, and drive organic traffic." },
  { icon: Server,     title: "HOSTING",        body: "Fast, secure, reliable hosting built for performance." },
  { icon: Palette,    title: "BRANDING",       body: "Build a brand that stands out and connects with your audience." },
  { icon: Headphones, title: "SUPPORT",        body: "Real support from real people when your business needs help." },
];

const STATS = [
  { icon: Users,        value: "250+",     label: "HAPPY CLIENTS" },
  { icon: Monitor,      value: "500+",     label: "WEBSITES LAUNCHED" },
  { icon: MessageSquare,value: "10,000+",  label: "AI CONVERSATIONS" },
  { icon: TrendingUp,   value: "200%+",    label: "AVERAGE GROWTH" },
];

const TRUST = ["Google", "Meta", "Shopify", "WordPress", "Stripe", "Klaviyo", "Hostinger"];

function upsertMeta(name, content, isProperty = false) {
  const attr = isProperty ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function upsertCanonical(href) {
  let el = document.querySelector("link[rel='canonical']");
  if (!el) { el = document.createElement("link"); el.rel = "canonical"; document.head.appendChild(el); }
  el.href = href;
}

export default function Custom() {
  useEffect(() => {
    const origin = process.env.REACT_APP_SITE_URL || window.location.origin;
    document.title = "Custom — PlugIt AI · Websites that work. Bots that sell. Brands that grow.";
    upsertMeta("description", "PlugIt AI's custom-tier build. Premium web design, AI automation, SEO, hosting, branding, and real support — engineered for businesses that want to plug in, power up, and grow smarter.");
    upsertMeta("og:title", "Custom — PlugIt AI", true);
    upsertMeta("og:description", "Plug in. Power up. Grow smarter.", true);
    upsertCanonical(`${origin}/custom`);
  }, []);

  return (
    <div className="min-h-screen w-full pb-24 md:pb-0 overflow-x-hidden"
         style={{ background: INK, color: SILVER, fontFamily: "'Inter','Cabinet Grotesk',system-ui,sans-serif" }}
         data-testid="custom-page">

      {/* Charcoal texture bg */}
      <div aria-hidden className="pointer-events-none fixed inset-0 z-0" style={{
        background: `radial-gradient(1400px 800px at 80% 5%, ${BLUE_GLOW}, transparent 55%),
                     radial-gradient(900px 500px at 20% 90%, rgba(0,163,255,0.14), transparent 60%),
                     linear-gradient(180deg, ${INK} 0%, ${CHAR} 100%)`,
      }} />
      <div aria-hidden className="pointer-events-none fixed inset-0 z-0 opacity-[0.04]" style={{
        background: "repeating-linear-gradient(0deg, rgba(255,255,255,0.6) 0px, rgba(255,255,255,0.6) 1px, transparent 1px, transparent 3px)",
      }} />

      <TopNav />

      {/* HERO */}
      <section className="relative z-10 pt-28 md:pt-36 pb-14 md:pb-20">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-[1.05fr,0.95fr] items-center gap-10 md:gap-14">
          {/* LEFT — headline */}
          <div>
            <div className="text-[11px] md:text-[12px] font-black uppercase tracking-[0.32em] mb-5" style={{ color: BLUE_HI }}>
              Plug in. Power up. Grow smarter.
            </div>
            <h1
              className="font-black tracking-[-0.015em] leading-[0.94] uppercase"
              style={{ fontSize: "clamp(2.4rem, 5.6vw, 5rem)" }}
              data-testid="custom-h1"
            >
              <span style={{
                background: `linear-gradient(180deg, ${SILVER_HI} 0%, #8A8E96 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                textShadow: `0 1px 0 rgba(255,255,255,0.05)`,
              }}>Websites that work.</span><br/>
              <span style={{
                background: `linear-gradient(180deg, ${SILVER_HI} 0%, #8A8E96 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}>Bots that </span>
              <span style={{ color: BLUE, textShadow: `0 0 30px ${BLUE_GLOW}, 0 0 2px rgba(0,163,255,0.6)` }}>sell.</span><br/>
              <span style={{
                background: `linear-gradient(180deg, ${SILVER_HI} 0%, #8A8E96 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}>Brands that </span>
              <span style={{ color: BLUE, textShadow: `0 0 30px ${BLUE_GLOW}, 0 0 2px rgba(0,163,255,0.6)` }}>grow.</span>
            </h1>

            <p className="mt-6 text-base md:text-lg leading-relaxed max-w-[560px]" style={{ color: DIM }}>
              PlugItAI builds high-performance websites and AI-powered solutions that help
              businesses automate, convert, and scale.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a href="tel:+12148155966" className="inline-flex items-center gap-2 h-14 px-6 rounded-md font-black text-sm uppercase tracking-[0.18em]"
                 style={{
                   background: `linear-gradient(135deg, ${GOLD_HI} 0%, ${GOLD} 100%)`,
                   color: "#0A0A0C",
                   boxShadow: `0 18px 40px -14px ${GOLD}88, inset 0 1px 0 rgba(255,255,255,0.3)`,
                 }}
                 data-testid="custom-cta-primary">
                Get your website <ArrowRight size={16} />
              </a>
              <button type="button"
                      className="inline-flex items-center gap-2 h-14 px-6 rounded-md font-bold text-sm uppercase tracking-[0.18em]"
                      style={{
                        background: "rgba(255,255,255,0.02)",
                        border: `1px solid ${BLUE}55`,
                        color: SILVER_HI,
                      }}
                      data-testid="custom-cta-video">
                <PlayCircle size={18} style={{ color: BLUE_HI }}/> Watch video
              </button>
            </div>
          </div>

          {/* RIGHT — outlet centerpiece */}
          <div className="relative order-first lg:order-last">
            <OutletVisual />
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="relative z-10 pb-14 md:pb-20">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10">
          <div className="text-center text-[11px] font-black uppercase tracking-[0.32em] mb-5" style={{ color: BLUE_HI }}>
            Trusted by businesses & brands
          </div>
          <div className="rounded-xl border px-6 md:px-10 py-6 flex flex-wrap items-center justify-around gap-x-8 gap-y-4"
               style={{ background: PANEL, borderColor: "rgba(199,203,209,0.12)" }}>
            {TRUST.map((brand) => (
              <span key={brand} className="text-sm md:text-base font-bold" style={{ color: SILVER, letterSpacing: "0.05em" }}>
                {brand}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="relative z-10 pb-14 md:pb-20">
        <div className="mx-auto max-w-[1280px] px-6 md:px-10">
          <div className="flex items-center gap-3 justify-center mb-10">
            <span className="h-px flex-1 max-w-[80px]" style={{ background: `linear-gradient(90deg, transparent, ${BLUE}66, transparent)` }}/>
            <h2 className="font-black uppercase tracking-[-0.01em] text-center" style={{ fontSize: "clamp(1.4rem, 3vw, 2.2rem)", color: SILVER_HI }}>
              What we{" "}
              <span style={{ color: BLUE, textShadow: `0 0 20px ${BLUE_GLOW}` }}>plug into</span>{" "}
              your business
            </h2>
            <span className="h-px flex-1 max-w-[80px]" style={{ background: `linear-gradient(90deg, transparent, ${BLUE}66, transparent)` }}/>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {SERVICES.map((s) => (
              <ServiceCard key={s.title} icon={s.icon} title={s.title} body={s.body} />
            ))}
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="relative z-10 pb-24 md:pb-32">
        <div className="mx-auto max-w-[1280px] px-6 md:px-10">
          <div className="rounded-xl border px-4 md:px-8 py-5 md:py-6 grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4"
               style={{ background: PANEL, borderColor: "rgba(0,163,255,0.28)", boxShadow: `inset 0 0 60px -20px ${BLUE_GLOW}` }}>
            {STATS.map(({ icon: I, value, label }) => (
              <div key={label} className="flex items-center gap-3">
                <I size={26} style={{ color: BLUE, filter: `drop-shadow(0 0 8px ${BLUE_GLOW})` }} strokeWidth={1.5}/>
                <div>
                  <div className="text-2xl md:text-3xl font-black leading-none" style={{ color: BLUE, textShadow: `0 0 14px ${BLUE_GLOW}` }}>
                    {value}
                  </div>
                  <div className="mt-1 text-[9px] md:text-[10px] font-black uppercase tracking-[0.24em]" style={{ color: SILVER }}>
                    {label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PlugitAIChat />
      <MobilePocketPopup />
    </div>
  );
}

function TopNav() {
  return (
    <header className="fixed top-0 left-0 right-0 z-50"
            style={{ background: "rgba(10,10,12,0.86)", backdropFilter: "blur(14px)", borderBottom: `1px solid rgba(0,163,255,0.18)` }}>
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src={BRAND.logos.plugitai} alt="PlugItAI" className="h-9 md:h-11 w-auto" />
        </Link>
        <nav className="hidden md:flex items-center gap-1">
          {[
            { to: "/", label: "Home" },
            { to: "/for", label: "Services" },
            { to: "#showcase", label: "Portfolio" },
            { to: "/about", label: "About Us" },
            { to: "#pricing", label: "Pricing" },
            { to: "tel:+12148155966", label: "Contact" },
          ].map((n) => (
            <Link key={n.label} to={n.to}
                  className="px-3 h-10 inline-flex items-center rounded-md text-[11px] font-black uppercase tracking-[0.22em] hover:bg-white/[0.04] transition-colors"
                  style={{ color: SILVER }}>
              {n.label}
            </Link>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-4">
          <div className="text-right leading-tight">
            <div className="text-[9px] font-black uppercase tracking-[0.32em]" style={{ color: BLUE_HI }}>Let&apos;s power up</div>
            <a href="tel:+12148155966" className="inline-flex items-center gap-1 text-sm font-bold" style={{ color: SILVER_HI }}>
              <Phone size={12} style={{ color: BLUE_HI }}/> (214) 815-5966
            </a>
          </div>
          <a href="tel:+12148155966" className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-[11px] font-black uppercase tracking-[0.22em] border"
             style={{ borderColor: BLUE, color: SILVER_HI, background: "rgba(0,163,255,0.06)" }}>
            Get started
          </a>
        </div>
      </div>
    </header>
  );
}

function ServiceCard({ icon: Icon, title, body }) {
  return (
    <div className="rounded-lg border p-5 md:p-6 transition-all hover:-translate-y-0.5 hover:border-[rgba(0,163,255,0.55)]"
         style={{ background: PANEL, borderColor: "rgba(199,203,209,0.12)", boxShadow: `inset 0 0 40px -20px ${BLUE_GLOW}` }}
         data-testid={`custom-service-${title.toLowerCase().replace(/\s+/g,"-")}`}>
      <div className="w-12 h-12 rounded-md flex items-center justify-center mb-4"
           style={{ background: "rgba(0,163,255,0.06)", border: `1px solid ${BLUE}55` }}>
        <Icon size={22} style={{ color: BLUE, filter: `drop-shadow(0 0 6px ${BLUE_GLOW})` }} strokeWidth={1.75}/>
      </div>
      <h3 className="font-black uppercase tracking-tight text-lg md:text-xl mb-2" style={{ color: SILVER_HI }}>{title}</h3>
      <p className="text-sm leading-relaxed" style={{ color: DIM }}>{body}</p>
      <a href="tel:+12148155966"
         className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-[0.24em]"
         style={{ color: GOLD }}>
        Learn more <ArrowRight size={12}/>
      </a>
    </div>
  );
}

function OutletVisual() {
  return (
    <div className="relative aspect-square max-h-[540px] mx-auto">
      {/* Ambient blue glow */}
      <div aria-hidden className="absolute inset-0 pointer-events-none"
           style={{ background: `radial-gradient(360px 300px at 50% 50%, ${BLUE_GLOW}, transparent 65%)` }}/>

      {/* Outlet plate */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative"
             style={{
               width: "62%",
               height: "78%",
               background: "linear-gradient(180deg, #22242A 0%, #0F1013 100%)",
               borderRadius: 22,
               boxShadow: `0 40px 100px -30px ${BLUE_GLOW}, inset 0 2px 0 rgba(255,255,255,0.08), inset 0 -2px 0 rgba(0,0,0,0.7)`,
               border: `1px solid rgba(255,255,255,0.06)`,
             }}>
          {/* Screws */}
          {["top","bottom"].map((p) => (
            <div key={p} className="absolute left-1/2 -translate-x-1/2 w-3 h-3 rounded-full"
                 style={{ [p]: 10, background: "radial-gradient(circle at 30% 30%, #6a6d75, #22242a 70%)", boxShadow: "inset 0 1px 1px rgba(0,0,0,0.6)" }}/>
          ))}
          {/* Blue outlet ring */}
          <div className="absolute left-1/2 top-[24%] -translate-x-1/2 rounded-2xl"
               style={{
                 width: "60%", height: "56%",
                 border: `2px solid ${BLUE}`,
                 boxShadow: `0 0 30px ${BLUE_GLOW}, inset 0 0 20px ${BLUE_GLOW}`,
                 background: "radial-gradient(ellipse at center, rgba(0,163,255,0.08), rgba(0,0,0,0.7))",
               }}>
            {/* Prongs */}
            <div className="absolute left-1/2 top-[18%] -translate-x-[130%] w-1 h-6 rounded-sm" style={{ background: "#0a0a0c" }}/>
            <div className="absolute left-1/2 top-[18%] translate-x-[30%] w-1 h-6 rounded-sm" style={{ background: "#0a0a0c" }}/>
            <div className="absolute left-1/2 top-[62%] -translate-x-1/2 w-2 h-3 rounded-full" style={{ background: "#0a0a0c" }}/>
            {/* P logo center */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-black" style={{
              fontSize: "clamp(2.4rem, 5vw, 3.4rem)", color: BLUE_HI,
              textShadow: `0 0 20px ${BLUE_GLOW}, 0 0 4px ${BLUE}`,
              letterSpacing: "-0.02em",
            }}>P</div>
          </div>
        </div>
      </div>

      {/* Circuit lines shooting right */}
      <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full pointer-events-none z-10"
           style={{ filter: `drop-shadow(0 0 6px ${BLUE_GLOW})` }} aria-hidden>
        {[
          "M260,180 L340,180 L360,160 L400,160",
          "M260,200 L320,200 L340,220 L400,220",
          "M260,220 L350,220 L370,240 L400,240",
          "M260,240 L330,240 L350,260 L400,260",
          "M140,180 L60,180 L40,160 L0,160",
          "M140,220 L70,220 L40,240 L0,240",
        ].map((d, i) => (
          <path key={i} d={d} fill="none" stroke={BLUE} strokeWidth="1.5" opacity="0.75"/>
        ))}
        {/* Endpoint dots */}
        {[
          [395, 160], [395, 220], [395, 240], [395, 260],
          [5, 160], [5, 240],
        ].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="3" fill={BLUE_HI} opacity="0.9"/>
        ))}
      </svg>
    </div>
  );
}
